import { useMemo } from "react";
import {
  SpicesMap,
  NUTRIENTS,
  MAX_QUANTITY_GRAM,
  Cache,
  NutrientId,
} from "../data/schema";

export type useNutritionProps = {
  spices: SpicesMap;
  cache: Cache | undefined;
};

export type DailyValueInfo = {
  unit: string;
  value: number;
  percent: number;
};

export type NutrientWithTotal = {
  id: NutrientId;
  name: string;
  amount: number;
  unit: string;
  total: number;
  dv?: DailyValueInfo;
};

const totalDv = NUTRIENTS;

export function useNutrition({
  spices,
  cache,
}: useNutritionProps): NutrientWithTotal[] | null {
  const nutrition = useMemo(() => {
    if (!spices.size || !cache) return null;

    const summedNutrients: NutrientWithTotal[] = Array.from(spices)
      .map(([id, spice]) => {
        if (!cache?.[id] || !("nutrition" in cache[id])) {
          console.warn(`Spice with ID ${id} is missing nutrition data.`);
          return []; // skip this spice
        }

        const nutrition = cache[id]?.nutrition;
        if (!nutrition) return [];
        // scale each nutrient by the spice quantity
        return nutrition.map((n) => ({
          id: n.id as NutrientId,
          name: n.name,
          amount: n.amount * spice.quantity,
          unit: n.unit,
          total: n.amount * MAX_QUANTITY_GRAM,
        }));
      })
      .flat()
      .reduce((acc: NutrientWithTotal[], n) => {
        const existing = acc.find((x) => x.id === n.id);
        if (existing) existing.amount += n.amount;
        else acc.push(n);
        return acc;
      }, []);

    summedNutrients.forEach((n) => {
      const totalNutrient = totalDv.find((t) => t.id === n.id);
      if (totalNutrient) {
        n.dv = {
          unit: totalNutrient.dailyValue.unit,
          value: totalNutrient.dailyValue.value,
          percent: (n.amount / totalNutrient.dailyValue.value) * 100,
        };
      }
    });

    return summedNutrients;
  }, [spices, cache]);

  return nutrition;
}
